import React,{Suspense,lazy,useEffect} from 'react';
import {motion,useScroll,useTransform} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useMobilePerformance} from '../hooks/useMobilePerformance';
import SectionOptimizer from '../components/common/SectionOptimizer';

// Lazy load heavy components for mobile performance
const ChatMockup=lazy(()=> import('../components/ChatMockup'));
const DashboardSnippet=lazy(()=> import('../components/DashboardSnippet'));
const ROIComparison=lazy(()=> import('../components/ROIComparison'));
const TrustBar=lazy(()=> import('../components/TrustBar'));

const Home=()=> {
  useEffect(() => {
    // Canonical Optimization
    let link = document.querySelector("link[rel='canonical']");
    if (link) {
      link.setAttribute('href', 'https://getdmflow.com/');
    }
  }, []);

  const {isMobile,yOffset}=useMobilePerformance();
  const {scrollYProgress}=useScroll();
  
  // Disable complex parallax on mobile for performance
  const heroOpacity=useTransform(scrollYProgress,[0,0.2],[1,0]);
  const heroScale=useTransform(scrollYProgress,[0,0.2],[1,isMobile ? 1 : 0.98]);

  return (
    <div className="bg-brand-dark min-h-screen text-white overflow-x-hidden selection:bg-brand-cyan/30">
      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-20 lg:pt-52 lg:pb-40 flex items-center min-h-[90vh] lg:min-h-screen">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div className={`absolute top-[-5%] left-1/2 -translate-x-1/2 ${isMobile ? 'w-[600px] h-[400px] blur-[80px]' : 'w-[1400px] h-[800px] blur-[180px]'} bg-brand-blue/10 rounded-full`}></div>
          {!isMobile && <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-cyan/5 blur-[150px] rounded-full"></div>}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div 
              initial={{opacity: 0, y: isMobile ? 10 : 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, ease: "easeOut"}}
            >
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-cyan/20 bg-brand-cyan/5 text-brand-cyan text-[10px] font-black uppercase tracking-[0.25em] mb-8 shadow-[0_0_30px_rgba(34,211,238,0.15)]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-cyan opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-cyan"></span>
                </span>
                AI Sales Infrastructure
              </div>
              <h1 className="text-5xl sm:text-7xl lg:text-[100px] font-black tracking-tight leading-[0.9] mb-8 lg:mb-10">
                AI Sales Automation <br />
                <span className="text-gradient">For Growing Businesses.</span>
              </h1>
              <p className="text-lg lg:text-2xl text-brand-gray mb-10 lg:mb-12 max-w-xl leading-relaxed font-medium">
                The enterprise lead conversion engine that instantly qualifies inquiries and books high-value appointments across Instagram, WhatsApp, and SMS.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-5 lg:gap-6">
                <Link to="/demo" className="px-10 py-5 lg:px-12 lg:py-6 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan text-white font-black text-lg lg:text-xl hover:shadow-[0_0_60px_rgba(18,115,234,0.6)] hover:scale-[1.03] transition-all duration-500 flex items-center justify-center gap-4 group">
                  Book A Demo
                  <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>

              <Suspense fallback={<div className="h-20" />}>
                <TrustBar />
              </Suspense>
            </motion.div>

            <div className="relative mt-12 lg:mt-0">
              <Suspense fallback={<div className="aspect-[9/16] bg-white/5 rounded-[3rem] animate-pulse" />}>
                <div className="relative z-20 scale-90 sm:scale-100 origin-center lg:origin-right">
                  <ChatMockup />
                </div>
              </Suspense>
              {!isMobile && (
                <motion.div 
                  animate={{y: [0, -10, 0]}}
                  transition={{duration: 4, repeat: Infinity}}
                  className="absolute -top-10 -left-12 z-30"
                >
                  <div className="glass-panel px-4 py-2 rounded-full border-green-500/30 bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                    Appointment Booked
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2. REVENUE VELOCITY */}
      <section className="py-24 lg:py-40 relative bg-[#060918] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-32">
            <SectionOptimizer>
              <h2 className="text-4xl lg:text-7xl font-black text-white mb-8 leading-tight uppercase tracking-tighter">
                AI Built for <br/> <span className="text-gradient">Revenue Velocity.</span>
              </h2>
              <p className="text-xl text-brand-gray font-medium leading-relaxed mb-12">
                Speed-to-lead is critical. Our AI sales agents engage 100% of your incoming traffic in under 1 second.
              </p>
              <div className="grid grid-cols-2 gap-4 lg:gap-6">
                <div className="p-6 glass-panel rounded-3xl">
                  <p className="text-3xl lg:text-4xl font-black text-white mb-2">0.8s</p>
                  <p className="text-[10px] text-brand-gray font-black uppercase tracking-widest">Avg. Response</p>
                </div>
                <div className="p-6 glass-panel rounded-3xl">
                  <p className="text-3xl lg:text-4xl font-black text-brand-cyan mb-2">94%</p>
                  <p className="text-[10px] text-brand-gray font-black uppercase tracking-widest">Lead Engagement</p>
                </div>
              </div>
            </SectionOptimizer>
            <SectionOptimizer delay={0.2}>
              <Suspense fallback={<div className="h-64 bg-white/5 rounded-3xl animate-pulse" />}>
                <DashboardSnippet />
              </Suspense>
            </SectionOptimizer>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SectionOptimizer delay={0.1} className="glass-panel p-8 lg:p-10 rounded-[2.5rem] border-white/5">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/20 flex items-center justify-center text-brand-cyan">
                  <SafeIcon icon={FiIcons.FiHeart} className="w-6 h-6" />
                </div>
                <h3 className="font-black text-white uppercase tracking-wider">Med Spa Agent</h3>
              </div>
              <p className="text-brand-gray text-sm font-medium leading-relaxed">Instantly qualifies "Botox pricing" DMs and books consultations 24/7.</p>
            </SectionOptimizer>
            <SectionOptimizer delay={0.2} className="glass-panel p-8 lg:p-10 rounded-[2.5rem] border-white/5">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/20 flex items-center justify-center text-brand-cyan">
                  <SafeIcon icon={FiIcons.FiHome} className="w-6 h-6" />
                </div>
                <h3 className="font-black text-white uppercase tracking-wider">Roofing Agent</h3>
              </div>
              <p className="text-brand-gray text-sm font-medium leading-relaxed">Qualifies storm damage leads and schedules inspections automatically.</p>
            </SectionOptimizer>
            <SectionOptimizer delay={0.3} className="glass-panel p-8 lg:p-10 rounded-[2.5rem] border-brand-cyan/20 bg-brand-cyan/5">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-brand-cyan text-brand-dark flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiZap} className="w-6 h-6" />
                </div>
                <h3 className="font-black text-white uppercase tracking-wider">DFY Launch</h3>
              </div>
              <p className="text-brand-gray text-sm font-medium leading-relaxed">We handle the full implementation, AI training, and CRM integration.</p>
            </SectionOptimizer>
          </div>
        </div>
      </section>

      {/* 3. ROI SECTION */}
      <section className="py-32 lg:py-48 relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <SectionOptimizer className="text-center mb-24">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-8">
              Critical Sales Insight
            </div>
            <h2 className="text-4xl lg:text-8xl font-black text-white mb-10 leading-tight uppercase tracking-tighter">
              78% of customers <br/> <span className="text-gradient">buy from the first reply.</span>
            </h2>
          </SectionOptimizer>
          <SectionOptimizer delay={0.2}>
            <Suspense fallback={<div className="h-96 bg-white/5 rounded-[3rem] animate-pulse" />}>
              <ROIComparison />
            </Suspense>
          </SectionOptimizer>
        </div>
      </section>
    </div>
  );
};

export default Home;